import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { LockClosedIcon, PencilIcon } from "@heroicons/react/24/outline";
import { cn } from "helpers/classNames";
import { forwardRef, useEffect } from "react";
import {
  TransportSedel,
  TransportSedelStatus,
} from "types/transportSedel.type";
import { useSignatureModalLogic } from "../useSignatureModalLogic";
import { OperatorSignatureSheet } from "./OperatorSignatureSheet";
import { Uppdrag } from "types/uppdrag.type";

const MISSING_DRIVER_TEXT =
  "Tillgänglig när en chaufför är kopplad till uppdraget";
import { SignatureRequirementKey } from "./loadingRequirements";

type Props = {
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
  isHighlighted?: boolean;
  signatureRequest?: SignatureRequirementKey | null;
  onSignatureRequestHandled?: () => void;
};

export const OperatorSignatureSection = forwardRef<HTMLElement, Props>(
  (
    {
      transportSedel,
      uppdrag,
      isHighlighted,
      signatureRequest,
      onSignatureRequestHandled,
    },
    ref,
  ) => {
    const isMissingDriver = !uppdrag.assignedToUser;

    return (
      <section
        ref={ref}
        className={cn(
          "scroll-mt-24 rounded-xl transition-shadow duration-300",
          isHighlighted && "ring-2 ring-blue-400 ring-offset-4",
        )}
      >
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Signaturer
        </h4>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <SignatureCard
            label="Chaufför"
            transportSedel={transportSedel}
            fieldName="driverSignature"
            isDisabled={isMissingDriver}
            disabledText={MISSING_DRIVER_TEXT}
            shouldOpenModal={signatureRequest === "driverSignature"}
            onOpenModalHandled={onSignatureRequestHandled}
          />
          <SignatureCard
            label="Lämnare"
            transportSedel={transportSedel}
            fieldName="customerSignature"
            notAvailableFieldName="customerSignatureUnavailable"
            shouldOpenModal={signatureRequest === "customerSignature"}
            onOpenModalHandled={onSignatureRequestHandled}
          />
          <SignatureCard
            label="Mottagare"
            transportSedel={transportSedel}
            fieldName="wasteStationSignature"
            notAvailableFieldName="wasteStationSignatureUnavailable"
            isDisabled={transportSedel.status === TransportSedelStatus.Created}
            disabledText="Tillgänglig när transportsedeln är lastad"
            shouldOpenModal={signatureRequest === "wasteStationSignature"}
            onOpenModalHandled={onSignatureRequestHandled}
          />
        </div>
      </section>
    );
  },
);
OperatorSignatureSection.displayName = "OperatorSignatureSection";

type CardProps = {
  label: string;
  transportSedel: TransportSedel;
  fieldName: keyof TransportSedel;
  notAvailableFieldName?: keyof TransportSedel;
  isDisabled?: boolean;
  disabledText?: string;
  shouldOpenModal?: boolean;
  onOpenModalHandled?: () => void;
};

const SignatureCard = ({
  label,
  transportSedel,
  fieldName,
  notAvailableFieldName,
  isDisabled,
  disabledText,
  shouldOpenModal,
  onOpenModalHandled,
}: CardProps) => {
  const logic = useSignatureModalLogic(
    transportSedel,
    fieldName,
    notAvailableFieldName,
  );

  // Öppnas utifrån (checklistan) via request-props
  const { openModal } = logic;
  useEffect(() => {
    if (!shouldOpenModal) return;
    if (!isDisabled) {
      openModal();
    }
    onOpenModalHandled?.();
  }, [shouldOpenModal, isDisabled, openModal, onOpenModalHandled]);

  const signature = transportSedel[fieldName] as string | undefined | null;
  const isNotAvailable =
    !!notAvailableFieldName && !!transportSedel[notAvailableFieldName];
  const isDone = !!signature || isNotAvailable;

  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border p-3",
        isDone && "border-gray-75 bg-white",
        !isDone && isDisabled && "border-gray-75 bg-gray-12",
        !isDone && !isDisabled && "border-dashed border-blue-300 bg-blue-50/40",
      )}
    >
      <div className="flex items-center justify-between gap-x-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        {isDone && (
          <CheckCircleIcon
            className="h-5 w-5 shrink-0 text-green-600"
            aria-hidden="true"
          />
        )}
      </div>

      {isDisabled && !isDone ? (
        <p className="mt-2 flex items-start gap-x-1.5 text-sm text-gray-400">
          <LockClosedIcon
            className="mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          {disabledText}
        </p>
      ) : (
        <>
          {isDone ? (
            <button
              type="button"
              onClick={logic.openModal}
              className="mt-2 flex h-16 w-full items-center justify-center rounded-lg bg-gray-12 transition-colors active:bg-gray-25"
            >
              {signature ? (
                <img
                  src={signature}
                  alt={`Signatur ${label.toLowerCase()}`}
                  className="max-h-12 w-auto max-w-[200px]"
                />
              ) : (
                <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                  Ej tillgänglig
                </span>
              )}
            </button>
          ) : (
            <div className="mt-2 flex flex-col gap-y-1.5">
              <button
                type="button"
                onClick={logic.openModal}
                className="flex h-11 w-full items-center justify-center gap-x-1.5 rounded-lg bg-blue-600 text-sm font-semibold text-white shadow-sm transition-colors active:bg-blue-700"
              >
                <PencilIcon className="h-4 w-4" aria-hidden="true" />
                Signera
              </button>
              {!!notAvailableFieldName && (
                <button
                  type="button"
                  onClick={logic.onNotAvailableSubmit}
                  className="h-9 w-full rounded-lg text-xs font-semibold text-gray-500 transition-colors active:bg-gray-25 active:text-gray-700"
                >
                  Ej tillgänglig
                </button>
              )}
            </div>
          )}
        </>
      )}

      <OperatorSignatureSheet
        isOpen={logic.isModalOpen}
        title={`Signatur ${label.toLowerCase()}`}
        onClose={logic.closeModal}
        onSubmit={logic.onSubmit}
        isPending={logic.isPending}
        existingSignatureInBase64={signature}
      />
    </div>
  );
};
